import * as React from "react"

import { cn } from "@/lib/utils"

// Date/time input types whose native picker normally only opens when the tiny
// calendar/clock icon is tapped. On a kiosk touchscreen that icon is an
// unreliably small target, so for these types we open the picker when the user
// taps anywhere inside the field (or presses Enter/Space while it's focused).
const PICKER_TYPES = new Set(["date", "datetime-local", "month", "week", "time"]);

// showPicker() must be invoked from a user gesture (click / keydown both
// qualify). It can still throw — unsupported browser, disabled/readonly input,
// or a picker that's already open — so every call is guarded. On failure the
// native icon remains fully functional, so there is no regression.
function openNativePicker(el) {
  if (!el || typeof el.showPicker !== "function") return;
  try {
    el.showPicker();
  } catch {
    /* not allowed / unsupported — native icon still opens the picker */
  }
}

const Input = React.forwardRef(
  ({ className, type, onWheel, onFocus, onClick, onKeyDown, ...props }, ref) => {
    const isNumber = type === "number";
    const isPicker = PICKER_TYPES.has(type);

    const handleWheel = isNumber
      ? (e) => { e.currentTarget.blur(); onWheel?.(e); }
      : onWheel;

    const handleFocus = isNumber
      ? (e) => { e.currentTarget.select(); onFocus?.(e); }
      : onFocus;

    // Bug 1 — make the whole field a tap target for the native picker, not just
    // the icon. Applies to every date/time field app-wide via the shared Input.
    const handleClick = isPicker
      ? (e) => { openNativePicker(e.currentTarget); onClick?.(e); }
      : onClick;

    // Keyboard accessibility: the field is still focusable and Enter/Space open
    // the picker (preventing Space from scrolling the page).
    const handleKeyDown = isPicker
      ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openNativePicker(e.currentTarget);
          }
          onKeyDown?.(e);
        }
      : onKeyDown;

    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-lg border border-input bg-transparent px-3 py-1 text-base transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          isPicker && "cursor-pointer",
          className
        )}
        ref={ref}
        onWheel={handleWheel}
        onFocus={handleFocus}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...props} />
    );
  }
)
Input.displayName = "Input"

export { Input }
